import { format } from "date-fns";
import axios from "axios";
import ModalConfirm from "../../../components/ModalConfirm";
import { WarningRounded } from "@material-ui/icons";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { ButtonAction } from "../../../constants/subscription";
import { getClientAccessStatus } from "../../../api";
import { config } from "../../../../config/envs";
import { JdsButton } from "../../../components/jds/JdsButton";
import "../../../components/jds/jds.css";

function getButtonAction(accessStatus) {
  if (!accessStatus) return null;
  if (accessStatus.isBlocked) return ButtonAction.UNBLOCK;
  return ButtonAction.BLOCK;
}

export default function ClientAccessToggle({ clientId }) {
  const dispatch = useDispatch();

  const [isBlockModalOpen, setIsBlockModalOpen] = useState(false);
  const [isUnblockModalOpen, setIsUnblockModalOpen] = useState(false);
  const [accessStatus, setAccessStatus] = useState(null);
  const [hasFetchedAccessStatus, setHasFetchedAccessStatus] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [blockExpiresAt, setBlockExpiresAt] = useState("");
  const [isIndefiniteBlock, setIsIndefiniteBlock] = useState(false);

  const clientSubscription = useSelector(
    state => state.app.client_subscription
  );

  const requestIdRef = useRef(0);

  const fetchAccessStatus = useCallback(async () => {
    const requestId = ++requestIdRef.current;
    setAccessStatus(null);
    setHasFetchedAccessStatus(false);
    if (!clientId) return;
    try {
      const response = await getClientAccessStatus(clientId);
      if (requestIdRef.current !== requestId) return;
      setAccessStatus(response.data?.data ?? null);
    } catch (error) {
      if (requestIdRef.current !== requestId) return;
      console.warn("Falha ao buscar o status de acesso do cliente.", error);
      setAccessStatus(null);
    } finally {
      if (requestIdRef.current === requestId) {
        setHasFetchedAccessStatus(true);
      }
    }
  }, [clientId]);

  useEffect(() => {
    fetchAccessStatus();
  }, [fetchAccessStatus]);

  const buttonAction = getButtonAction(accessStatus);

  const blockUser = async () => {
    if (!clientId) {
      toast.error("Cliente ainda não carregado, aguarde e tente novamente.");
      return;
    }
    if (!isIndefiniteBlock && !blockExpiresAt) {
      toast.error("Informe a data de expiração do bloqueio ou marque bloqueio indefinido.");
      return;
    }
    setIsLoading(true);
    let payload = {
      clientId: clientId,
      expires_at: isIndefiniteBlock
        ? new Date("3000-01-01T23:59:59").toISOString()
        : new Date(blockExpiresAt + "T23:59:59").toISOString(),
      reason: "Bloqueio do acesso a plataforma pelo Admin."
    };

    try {
      const response = await axios.post(
        config.app.adminBaseUrl + "/user-access-control/block",
        payload
      );

      if (response.status < 200 || response.status >= 300) {
        toast.error(response.data?.message ?? "Erro ao bloquear usuário.");
      } else {
        toast.success(
          response.data?.message ?? "Usuário bloqueado com sucesso."
        );
        await fetchAccessStatus();
        dispatch({
          type: "LOAD_CLIENT_SUBSCRIPTION",
          payload: {
            id: payload.clientId,
            useV2: !!clientSubscription?.external_id
          }
        });
      }
    } catch (err) {
      const details = err.response?.data?.details;
      const detailMessage = details
        ? Object.values(details).join(" ")
        : null;
      toast.error(
        err.response?.data?.message ||
          detailMessage ||
          "Ocorreu um erro ao bloquear o usuário, por favor, tente novamente."
      );
    } finally {
      setIsLoading(false);
      setIsBlockModalOpen(false);
    }
  };

  const unblockUser = async () => {
    if (!clientId) {
      toast.error("Cliente ainda não carregado, aguarde e tente novamente.");
      return;
    }
    setIsLoading(true);
    let payload = {
      clientId: clientId
    };

    try {
      const response = await axios.post(
        config.app.adminBaseUrl + "/user-access-control/unblock",
        payload
      );
      if (response.status < 200 || response.status >= 300) {
        toast.error(response.data?.message ?? "Erro ao desbloquear usuário.");
      } else {
        toast.success(
          response.data?.message ?? "Usuário desbloqueado com sucesso."
        );
        await fetchAccessStatus();
        dispatch({
          type: "LOAD_CLIENT_SUBSCRIPTION",
          payload: {
            id: payload.clientId,
            useV2: !!clientSubscription?.external_id
          }
        });
      }
    } catch (err) {
      const details = err.response?.data?.details;
      const detailMessage = details
        ? Object.values(details).join(" ")
        : null;
      toast.error(
        err.response?.data?.message ||
          detailMessage ||
          "Ocorreu um erro ao desbloquear o usuário, por favor, tente novamente."
      );
    } finally {
      setIsLoading(false);
      setIsUnblockModalOpen(false);
    }
  };

  return (
    <div className="jds-scope">
      {hasFetchedAccessStatus && !buttonAction && (
        <span className="text-muted">Status de acesso indisponível</span>
      )}

      {buttonAction === "block" && (
        <JdsButton
          variant="outlined"
          color="error"
          onClick={() => setIsBlockModalOpen(true)}
          disabled={isLoading}
        >
          Bloquear acesso
        </JdsButton>
      )}

      {buttonAction === "unblock" && (
        <JdsButton
          onClick={() => setIsUnblockModalOpen(true)}
          disabled={isLoading}
        >
          Desbloquear acesso
        </JdsButton>
      )}

      {isUnblockModalOpen && (
        <ModalConfirm
          icon={WarningRounded}
          title="Desbloquear acesso do cliente?"
          message="Esta ação permitirá que o cliente possa acessar a plataforma Jusfy até o final do período vigente."
          onConfirm={unblockUser}
          onClose={() => setIsUnblockModalOpen(false)}
          isLoading={isLoading}
        />
      )}

      {isBlockModalOpen && (
        <ModalConfirm
          icon={WarningRounded}
          title="Bloquear acesso do cliente?"
          message="Esta ação impedirá o cliente de acessar a plataforma Jusfy."
          onConfirm={blockUser}
          isLoading={isLoading}
          onClose={() => {
            setIsBlockModalOpen(false);
            setBlockExpiresAt("");
            setIsIndefiniteBlock(false);
          }}
        >
          <div style={{ marginBottom: 16, textAlign: "left" }}>
            <label
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                cursor: "pointer"
              }}
            >
              <input
                type="checkbox"
                style={{ width: 16, height: 16, margin: 0 }}
                checked={isIndefiniteBlock}
                onChange={e => {
                  setIsIndefiniteBlock(e.target.checked);
                  if (e.target.checked) {
                    setBlockExpiresAt("");
                  }
                }}
              />
              <strong>Bloqueio indefinido</strong>
            </label>

            {!isIndefiniteBlock && (
              <div style={{ marginTop: 12 }}>
                <label style={{ display: "block", marginBottom: 4, fontWeight: 600 }}>
                  Bloquear até quando?
                </label>
                <input
                  type="date"
                  className="form-control"
                  value={blockExpiresAt}
                  min={format(new Date(), "yyyy-MM-dd")}
                  onChange={e => setBlockExpiresAt(e.target.value)}
                />
              </div>
            )}
          </div>
        </ModalConfirm>
      )}
    </div>
  );
}
